import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Save, Info } from "lucide-react";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { Label } from "./ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./ui/card";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GeneralSettingsTab = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    includes_received_payments: true,
    includes_made_payments: false
  });
  const [hasChanges, setHasChanges] = useState(false);

  // Fetch current settings
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API}/settings/organization`);
        if (response.data?.bank_balance_defaults) {
          setSettings(response.data.bank_balance_defaults);
        }
      } catch (err) {
        console.error("Error fetching settings:", err);
        toast.error("Failed to load settings");
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/settings/organization`, {
        bank_balance_defaults: settings
      });
      setHasChanges(false);
      toast.success("Settings saved successfully");
    } catch (err) {
      console.error("Error saving settings:", err);
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="general-settings-tab">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            Bank Balance Defaults
          </CardTitle>
          <CardDescription>
            Set the default values for bank balance entry timing. 
            These values are automatically applied to new entries on the Bank Balance page.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Info banner */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex gap-3">
            <Info className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">Why is this important?</p>
              <p>
                On the same day, payments can occur both before and after the bank balance update. 
                By indicating which payments are already reflected in the balance, Floatly can 
                calculate the expected cash position more accurately.
              </p>
            </div>
          </div>

          {/* Checkbox: Includes received payments */}
          <div className="flex items-start gap-4 p-4 border rounded-lg">
            <Checkbox
              id="includes_received"
              checked={settings.includes_received_payments}
              onCheckedChange={(checked) => handleChange("includes_received_payments", checked)}
              data-testid="default-includes-received"
            />
            <div className="space-y-1">
              <Label htmlFor="includes_received" className="font-medium cursor-pointer">
                Includes received payments
              </Label>
              <p className="text-sm text-muted-foreground">
                The bank balance already includes received payments of that day. 
                This is the most common situation when you record the balance at the end of the day.
              </p>
            </div>
          </div>

          {/* Checkbox: Includes made payments */}
          <div className="flex items-start gap-4 p-4 border rounded-lg">
            <Checkbox
              id="includes_made"
              checked={settings.includes_made_payments}
              onCheckedChange={(checked) => handleChange("includes_made_payments", checked)}
              data-testid="default-includes-made"
            />
            <div className="space-y-1">
              <Label htmlFor="includes_made" className="font-medium cursor-pointer">
                Includes made payments
              </Label>
              <p className="text-sm text-muted-foreground">
                The bank balance already includes made payments of that day.
                Check this if you record the balance after all scheduled payments for that day have been executed.
              </p>
            </div>
          </div>

          {/* Save button */}
          <div className="flex justify-end pt-4 border-t">
            <Button
              onClick={handleSave}
              disabled={saving || !hasChanges}
              data-testid="save-general-settings"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? "Saving..." : "Save"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default GeneralSettingsTab;
